import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Divider,
  Avatar,
  makeStyles,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  Flag as FlagIcon,
  Print as PrintIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import api from "../../../services/api";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import HistoricoPrintTemplate from "./HistoricoPrintTemplate";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  timeline: {
    position: "relative",
    paddingLeft: theme.spacing(3),
    "&:before": {
      content: '""',
      position: "absolute",
      left: 20,
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: theme.palette.divider,
    },
  },
  timelineItem: {
    position: "relative",
    marginBottom: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    "&:before": {
      content: '""',
      position: "absolute",
      left: 11,
      top: 20,
      width: 18,
      height: 18,
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
      border: `3px solid ${theme.palette.background.paper}`,
    },
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
}));

const ClienteHistorico = ({ clienteId }) => {
  const classes = useStyles();
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [historicoEdit, setHistoricoEdit] = useState(null);
  const [contatos, setContatos] = useState([]);
  const [cliente, setCliente] = useState(null);
  const printRef = useRef();
  const [motivos, setMotivos] = useState([
    "VISITA",
    "ORCAMENTO",
    "FOLLOW_UP",
    "NEGOCIACAO",
    "FECHAMENTO",
    "POS_VENDA",
    "RECLAMACAO",
    "INDICACAO",
    "OUTRO",
  ]);
  const [formData, setFormData] = useState({
    data_visita: new Date().toISOString().split("T")[0],
    hora_visita: new Date().toTimeString().split(" ")[0].slice(0, 5),
    motivo_visita: "",
    contato_id: "",
    descricao: "",
    resultado: "",
    proxima_acao: "",
    data_proxima_acao: "",
    observacoes_internas: "",
    necessita_followup: false,
    data_followup: "",
    descricao_followup: "",
    status_followup: "PENDENTE",
    metas_vendas: "",
    potencial_vendas: "",
    acoes_vendas: "",
  });

  useEffect(() => {
    if (clienteId) {
      loadHistorico();
      loadContatos();
      loadCliente();
    }
  }, [clienteId]);

  const loadCliente = async () => {
    try {
      const { data } = await api.get(`/clientes/${clienteId}`);
      setCliente(data);
    } catch (error) {
      console.error("Erro ao carregar cliente:", error);
    }
  };

  const loadHistorico = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clientes/${clienteId}/historico`);
      // Garante que seja sempre um array e mapeia os campos do backend para o frontend
      const historicoMapeado = (Array.isArray(data.historicos) ? data.historicos : []).map(item => ({
        ...item,
        data_visita: item.data_contato,
        hora_visita: item.data_contato ? new Date(item.data_contato).toTimeString().split(" ")[0].slice(0, 5) : "",
        motivo_visita: item.meio_contato || "",
        contato_id: item.contatoId || "",
        descricao: item.observacoes || "",
        resultado: item.resultado_visita || "",
        // Campos diretos do banco de dados
        proxima_acao: item.proxima_acao || "",
        data_proxima_acao: item.data_proxima_acao || "",
        observacoes_internas: item.observacoes_internas || "",
        metas_vendas: item.metas_vendas || "",
        potencial_vendas: item.potencial_vendas || "",
        acoes_vendas: item.acoes_vendas || "",
      }));
      setHistorico(historicoMapeado);
    } catch (error) {
      console.error("Erro ao carregar histórico:", error);
      toast.error("Erro ao carregar histórico");
      setHistorico([]); // Define array vazio em caso de erro
    } finally {
      setLoading(false);
    }
  };

  const loadContatos = async () => {
    try {
      const { data } = await api.get(`/clientes/${clienteId}/contatos`);
      setContatos(data);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
    }
  };

  const handleOpenModal = (hist = null) => {
    if (hist) {
      // Debug: ver o que está vindo do backend
      console.log("📋 Dados do histórico recebido:", hist);
      
      setHistoricoEdit(hist);
      
      // Função auxiliar para validar e formatar data
      const formatarData = (dataString) => {
        if (!dataString || dataString === '0000-00-00' || dataString === '0000-00-00 00:00:00') {
          return "";
        }
        try {
          // Remove hora se houver (formato: 2025-10-13 21:00:00 -> 2025-10-13)
          const dataLimpa = dataString.includes(' ') ? dataString.split(' ')[0] : dataString.split('T')[0];
          
          // Valida se o formato está correto (YYYY-MM-DD)
          if (!/^\d{4}-\d{2}-\d{2}$/.test(dataLimpa)) {
            return "";
          }
          
          // Retorna diretamente a data no formato YYYY-MM-DD sem conversão de timezone
          return dataLimpa;
        } catch (error) {
          return "";
        }
      };

      // Função auxiliar para validar e formatar hora
      const formatarHora = (dataString) => {
        if (!dataString || dataString === '0000-00-00 00:00:00') {
          return "";
        }
        try {
          const dataObj = new Date(dataString);
          if (isNaN(dataObj.getTime())) {
            return "";
          }
          const horas = dataObj.getHours().toString().padStart(2, '0');
          const minutos = dataObj.getMinutes().toString().padStart(2, '0');
          return `${horas}:${minutos}`;
        } catch (error) {
          return "";
        }
      };

      // Função para normalizar status (minúscula -> MAIÚSCULA)
      const normalizarStatus = (status) => {
        if (!status) return "PENDENTE";
        const statusUpper = status.toUpperCase();
        // Valida se o status está nas opções disponíveis
        if (["PENDENTE", "CONCLUIDO", "CANCELADO"].includes(statusUpper)) {
          return statusUpper;
        }
        return "PENDENTE";
      };

      const dataVisita = hist.data_contato || hist.data_visita;
      
      setFormData({
        data_visita: formatarData(dataVisita) || new Date().toISOString().split("T")[0],
        hora_visita: formatarHora(dataVisita) || new Date().toTimeString().split(" ")[0].slice(0, 5),
        motivo_visita: (hist.meio_contato || hist.motivo_visita || "").toUpperCase(),
        contato_id: hist.contatoId || hist.contato_id || "",
        descricao: hist.observacoes || hist.descricao || "",
        resultado: hist.resultado_visita || hist.resultado || "",
        // Estes campos vêm dos dados extras ou diretamente do histórico
        proxima_acao: hist.proxima_acao || "",
        data_proxima_acao: formatarData(hist.data_proxima_acao) || "",
        observacoes_internas: hist.observacoes_internas || "",
        necessita_followup: hist.necessita_followup || false,
        // Follow-up: pega do array followups ou campos diretos
        data_followup: formatarData(
          (hist.followups && hist.followups.length > 0) 
            ? hist.followups[0].data_followup 
            : hist.data_followup
        ) || "",
        descricao_followup: 
          (hist.followups && hist.followups.length > 0)
            ? (hist.followups[0].descricao || hist.followups[0].assunto)
            : hist.descricao_followup || "",
        status_followup: normalizarStatus(
          (hist.followups && hist.followups.length > 0)
            ? hist.followups[0].status
            : hist.status_followup
        ),
        // Inteligência de Vendas: pega do array inteligenciaVendas ou campos diretos
        metas_vendas: 
          (hist.inteligenciaVendas && hist.inteligenciaVendas.length > 0)
            ? hist.inteligenciaVendas[0].produto
            : hist.metas_vendas || "",
        potencial_vendas: 
          (hist.inteligenciaVendas && hist.inteligenciaVendas.length > 0)
            ? hist.inteligenciaVendas[0].valor_estimado || ""
            : hist.potencial_vendas || "",
        acoes_vendas: 
          (hist.inteligenciaVendas && hist.inteligenciaVendas.length > 0)
            ? hist.inteligenciaVendas[0].objetivo_curto_prazo
            : hist.acoes_vendas || "",
      });
    } else {
      setHistoricoEdit(null);
      setFormData({
        data_visita: new Date().toISOString().split("T")[0],
        hora_visita: new Date().toTimeString().split(" ")[0].slice(0, 5),
        motivo_visita: "",
        contato_id: "",
        descricao: "",
        resultado: "",
        proxima_acao: "",
        data_proxima_acao: "",
        observacoes_internas: "",
        necessita_followup: false,
        data_followup: "",
        descricao_followup: "",
        status_followup: "PENDENTE",
        metas_vendas: "",
        potencial_vendas: "",
        acoes_vendas: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setHistoricoEdit(null);
  };

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    try {
      if (!formData.motivo_visita || !formData.descricao) {
        toast.error("Preencha os campos obrigatórios");
        return;
      }

      // Mapear os campos do frontend para o formato esperado pelo backend
      const dataHoraContato = `${formData.data_visita}T${formData.hora_visita}:00`;
      
      // Formatar data_proxima_acao para evitar problemas de timezone
      const dataProximaAcao = formData.data_proxima_acao 
        ? `${formData.data_proxima_acao}T12:00:00`
        : null;
      
      const payload = {
        clienteId: parseInt(clienteId),
        data_contato: dataHoraContato,
        meio_contato: formData.motivo_visita,
        observacoes: formData.descricao,
        resultado_visita: formData.resultado,
        proxima_acao: formData.proxima_acao || null,
        data_proxima_acao: dataProximaAcao,
        observacoes_internas: formData.observacoes_internas || null,
        potencial_vendas: formData.potencial_vendas ? parseFloat(formData.potencial_vendas.toString().replace(/[^\d,.-]/g, '').replace(',', '.')) : null,
        acoes_vendas: formData.acoes_vendas || null,
        status: "concluido",
        motivos: formData.motivo_visita ? [formData.motivo_visita] : []
      };

      // Só adicionar contatoId se tiver valor válido
      if (formData.contato_id && formData.contato_id !== "") {
        payload.contatoId = parseInt(formData.contato_id);
      }

      // Adicionar follow-up se preenchido
      if (formData.descricao_followup && formData.data_followup) {
        // Adiciona horário 12:00 para evitar problemas de timezone
        const dataFollowupComHora = `${formData.data_followup}T12:00:00`;
        
        payload.followup = {
          data_followup: dataFollowupComHora,
          assunto: formData.descricao_followup,
          descricao: formData.descricao_followup,
          status: (formData.status_followup || "PENDENTE").toLowerCase()
        };
        
        // Só adicionar contatoId se tiver valor válido
        if (formData.contato_id) {
          payload.followup.contatoId = parseInt(formData.contato_id);
        }
      }

      // Adicionar inteligência de vendas se preenchido
      if (formData.metas_vendas || formData.potencial_vendas || formData.acoes_vendas) {
        console.log("🎯 Salvando Inteligência de Vendas:", {
          metas_vendas: formData.metas_vendas,
          potencial_vendas: formData.potencial_vendas,
          acoes_vendas: formData.acoes_vendas
        });
        
        const inteligenciaVenda = {
          produto: formData.metas_vendas || "Não especificado"
        };
        
        // Só adicionar campos opcionais se tiverem valor
        if (formData.acoes_vendas && formData.acoes_vendas.trim() !== "") {
          inteligenciaVenda.objetivo_curto_prazo = formData.acoes_vendas;
        }
        
        if (formData.potencial_vendas && formData.potencial_vendas !== "") {
          const valorEstimado = parseFloat(formData.potencial_vendas.toString().replace(/[^\d,.-]/g, '').replace(',', '.'));
          // Só adicionar se for um número válido
          if (!isNaN(valorEstimado) && valorEstimado > 0) {
            inteligenciaVenda.valor_estimado = valorEstimado;
          }
        }
        
        console.log("📦 Objeto inteligenciaVenda final:", inteligenciaVenda);
        payload.inteligenciaVendas = [inteligenciaVenda];
      }

      console.log("📤 Payload completo a ser enviado:", payload);

      if (historicoEdit) {
        await api.put(
          `/clientes/${clienteId}/historico/${historicoEdit.id}`,
          payload
        );
        toast.success("Histórico atualizado com sucesso!");
      } else {
        await api.post(`/clientes/${clienteId}/historico`, payload);
        toast.success("Histórico registrado com sucesso!");
      }

      handleCloseModal();
      loadHistorico();
    } catch (error) {
      console.error("Erro ao salvar histórico:", error);
      toast.error(error.response?.data?.error || "Erro ao salvar histórico");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este registro?")) {
      return;
    }

    try {
      await api.delete(`/clientes/${clienteId}/historico/${id}`);
      toast.success("Registro excluído com sucesso!");
      loadHistorico();
    } catch (error) {
      console.error("Erro ao excluir histórico:", error);
      toast.error("Erro ao excluir histórico");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(parseISO(dateString), "dd/MM/yyyy 'às' HH:mm", {
        locale: ptBR,
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "CONCLUIDO":
        return "primary";
      case "PENDENTE":
        return "default";
      case "CANCELADO":
        return "secondary";
      default:
        return "default";
    }
  };

  // Configuração do react-to-print
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Historico_Relacionamento_${cliente?.NOMECLI || clienteId}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 5mm 3mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `
  });

  return (
    <Box>
      {/* Template de impressão (oculto na tela) */}
      <div style={{ display: "none" }}>
        <HistoricoPrintTemplate 
          ref={printRef} 
          cliente={cliente}
          historico={historico}
        />
      </div>

      <Box className={classes.header}>
        <Typography variant="h6">
          <HistoryIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
          Histórico de Relacionamento
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={historico.length === 0}
          >
            Imprimir
          </Button>
        </Box>
      </Box>

      <Box className={classes.timeline}>
        {historico.length === 0 ? (
          <Paper style={{ padding: 20, textAlign: "center" }}>
            <Typography color="textSecondary">
              Nenhum registro de histórico encontrado
            </Typography>
          </Paper>
        ) : (
          historico.map((item) => (
            <Box key={item.id} className={classes.timelineItem}>
              <Card className={classes.card}>
                <CardContent>
                  <Box className={classes.cardHeader}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {item.motivo_visita || item.meio_contato || "Sem motivo"}
                      </Typography>
                      <Box className={classes.userInfo}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(item.data_visita || item.data_contato)}
                        </Typography>
                      </Box>
                      {item.user && (
                        <Box className={classes.userInfo}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="textSecondary">
                            {item.user.name}
                          </Typography>
                        </Box>
                      )}
                      {item.contato && (
                        <Box className={classes.userInfo}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="textSecondary">
                            Contato: {item.contato.nome}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenModal(item)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider style={{ margin: "12px 0" }} />

                  <Typography variant="body1" paragraph>
                    <strong>Descrição:</strong> {item.descricao || item.observacoes || "Sem descrição"}
                  </Typography>

                  {(item.resultado || item.resultado_visita) && (
                    <Typography variant="body2" paragraph>
                      <strong>Resultado:</strong> {item.resultado || item.resultado_visita}
                    </Typography>
                  )}

                  {item.proxima_acao && (
                    <Box mb={1}>
                      <Chip
                        icon={<FlagIcon />}
                        label={`Próxima ação: ${item.proxima_acao}`}
                        color="primary"
                        variant="outlined"
                        className={classes.chip}
                      />
                      {item.data_proxima_acao && (
                        <Chip
                          label={`até ${formatDate(item.data_proxima_acao)?.split(' às ')?.[0] || item.data_proxima_acao}`}
                          size="small"
                          className={classes.chip}
                        />
                      )}
                    </Box>
                  )}

                  {(item.followup || (item.followups && item.followups.length > 0)) && (
                    <Box mt={2} p={2} bgcolor="action.hover" borderRadius={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Follow-up</strong>
                      </Typography>
                      {(item.followups?.[0]?.data_followup || item.followup?.data_followup) && (
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          <strong>Data:</strong>{" "}
                          {formatDate(item.followups?.[0]?.data_followup || item.followup?.data_followup)?.split(' às ')?.[0] || 
                           item.followups?.[0]?.data_followup || 
                           item.followup?.data_followup}
                        </Typography>
                      )}
                      <Typography variant="body2" paragraph>
                        <strong>Descrição:</strong>{" "}
                        {item.followups?.[0]?.descricao || 
                         item.followups?.[0]?.assunto || 
                         item.followup?.descricao_followup || 
                         "Sem descrição"}
                      </Typography>
                      <Box mt={1}>
                        <Chip
                          label={
                            (item.followups?.[0]?.status || 
                             item.followup?.status_followup || 
                             "PENDENTE").toUpperCase()
                          }
                          size="small"
                          color={getStatusColor(
                            item.followups?.[0]?.status || 
                            item.followup?.status_followup || 
                            "PENDENTE"
                          )}
                        />
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          ))
        )}
      </Box>

      {/* Modal de Cadastro/Edição */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {historicoEdit ? "Editar Registro" : "Novo Registro de Histórico"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Data *"
                fullWidth
                type="date"
                value={formData.data_visita}
                onChange={handleChange("data_visita")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Hora"
                fullWidth
                type="time"
                value={formData.hora_visita}
                onChange={handleChange("hora_visita")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Motivo *</InputLabel>
                <Select
                  value={formData.motivo_visita}
                  onChange={handleChange("motivo_visita")}
                >
                  {motivos.map((motivo) => (
                    <MenuItem key={motivo} value={motivo}>
                      {motivo.replace("_", " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Contato</InputLabel>
                <Select
                  value={formData.contato_id}
                  onChange={handleChange("contato_id")}
                >
                  <MenuItem value="">
                    <em>Nenhum contato específico</em>
                  </MenuItem>
                  {contatos.map((contato) => (
                    <MenuItem key={contato.id} value={contato.id}>
                      {contato.nome}{contato.cargo ? ` - ${contato.cargo}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Descrição *"
                fullWidth
                multiline
                rows={4}
                value={formData.descricao}
                onChange={handleChange("descricao")}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Resultado"
                fullWidth
                multiline
                rows={3}
                value={formData.resultado}
                onChange={handleChange("resultado")}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                label="Próxima Ação"
                fullWidth
                value={formData.proxima_acao}
                onChange={handleChange("proxima_acao")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Data da Próxima Ação"
                fullWidth
                type="date"
                value={formData.data_proxima_acao}
                onChange={handleChange("data_proxima_acao")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle1" style={{ marginTop: 16 }}>
                Follow-up
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Descrição do Follow-up"
                fullWidth
                multiline
                rows={2}
                value={formData.descricao_followup}
                onChange={handleChange("descricao_followup")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Data do Follow-up"
                fullWidth
                type="date"
                value={formData.data_followup}
                onChange={handleChange("data_followup")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status do Follow-up</InputLabel>
                <Select
                  value={formData.status_followup}
                  onChange={handleChange("status_followup")}
                >
                  <MenuItem value="PENDENTE">Pendente</MenuItem>
                  <MenuItem value="CONCLUIDO">Concluído</MenuItem>
                  <MenuItem value="CANCELADO">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle1" style={{ marginTop: 16 }}>
                Inteligência de Vendas
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Metas de Vendas"
                fullWidth
                multiline
                rows={2}
                value={formData.metas_vendas}
                onChange={handleChange("metas_vendas")}
              />
            </Grid>

            {/* <Grid item xs={12}>
              <TextField
                label="Potencial de Vendas"
                fullWidth
                multiline
                rows={2}
                value={formData.potencial_vendas}
                onChange={handleChange("potencial_vendas")}
              />
            </Grid> */}

            {/* <Grid item xs={12}>
              <TextField
                label="Ações de Vendas"
                fullWidth
                multiline
                rows={2}
                value={formData.acoes_vendas}
                onChange={handleChange("acoes_vendas")}
              />
            </Grid> */}

            <Grid item xs={12}>
              <TextField
                label="Observações Internas"
                fullWidth
                multiline
                rows={2}
                value={formData.observacoes_internas}
                onChange={handleChange("observacoes_internas")}
                placeholder="Observações visíveis apenas internamente"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClienteHistorico;
